import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon
} from '@mui/icons-material'
import { Box3D } from './types'
import { BoxAction, newBox } from './boxReducer'

interface LabelHUDProps {
  selectedId?: string
  boxes: Box3D[]
  onBoxAction: (action: BoxAction) => void
  onSelectionChange?: (id?: string) => void
}

const LabelHUD: React.FC<LabelHUDProps> = ({
  selectedId,
  boxes,
  onBoxAction,
  onSelectionChange
}) => {
  const [localValues, setLocalValues] = useState<Partial<Box3D>>({})
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null)

  // Find the selected box
  const selectedBox = selectedId ? boxes.find(box => box.id === selectedId) : null

  // Update local values when selection changes
  useEffect(() => {
    if (selectedBox) {
      setLocalValues({
        cx: selectedBox.cx,
        cy: selectedBox.cy,
        cz: selectedBox.cz,
        lx: selectedBox.lx,
        ly: selectedBox.ly,
        lz: selectedBox.lz,
        yaw: selectedBox.yaw,
        cls: selectedBox.cls,
        locked: selectedBox.locked
      })
    } else {
      setLocalValues({})
    }
  }, [selectedBox])

  // Debounced update function
  const debouncedUpdate = useCallback((updates: Partial<Box3D>) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    const timer = setTimeout(() => {
      if (selectedId) {
        onBoxAction({
          type: 'update',
          id: selectedId,
          patch: updates
        })
      }
    }, 300) // 300ms debounce

    setDebounceTimer(timer)
  }, [selectedId, onBoxAction, debounceTimer])

  // Handle input changes
  const handleInputChange = (field: keyof Box3D, value: any) => {
    const newValues = { ...localValues, [field]: value }
    setLocalValues(newValues)
    debouncedUpdate({ [field]: value })
  }

  // Handle duplicate
  const handleDuplicate = () => {
    if (selectedBox) {
      const duplicatedBox = newBox({
        ...selectedBox,
        id: undefined, // newBox will generate a new ID
        cx: selectedBox.cx + 0.5, // Offset slightly
        cz: selectedBox.cz + 0.5
      })
      
      onBoxAction({
        type: 'create',
        box: duplicatedBox
      })
      
      // Select the new box
      onSelectionChange?.(duplicatedBox.id)
    }
  }

  // Handle delete
  const handleDelete = () => {
    if (selectedId) {
      onBoxAction({
        type: 'delete',
        id: selectedId
      })
      onSelectionChange?.(undefined)
    }
  }

  // Handle lock toggle
  const handleLockToggle = () => {
    if (selectedId) {
      onBoxAction({
        type: 'update',
        id: selectedId,
        patch: {
          locked: !selectedBox?.locked
        }
      })
    }
  }

  if (!selectedBox) {
    return null
  }

  return (
    <Card 
      sx={{ 
        position: 'absolute', 
        top: 20, 
        right: 20, 
        width: 320,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        border: '1px solid #444'
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
          Box Properties
        </Typography>

        {/* Class Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ color: 'white' }}>Class</InputLabel>
          <Select
            value={localValues.cls || 'unknown'}
            onChange={(e) => handleInputChange('cls', e.target.value)}
            sx={{ 
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#888' }
            }}
          >
            <MenuItem value="person">Person</MenuItem>
            <MenuItem value="bag">Bag</MenuItem>
            <MenuItem value="vehicle">Vehicle</MenuItem>
            <MenuItem value="unknown">Unknown</MenuItem>
          </Select>
        </FormControl>

        {/* Position and Size Inputs */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <TextField
              label="X"
              type="number"
              value={localValues.cx?.toFixed(2) || '0.00'}
              onChange={(e) => handleInputChange('cx', parseFloat(e.target.value) || 0)}
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Y"
              type="number"
              value={localValues.cy?.toFixed(2) || '0.00'}
              onChange={(e) => handleInputChange('cy', parseFloat(e.target.value) || 0)}
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Z"
              type="number"
              value={localValues.cz?.toFixed(2) || '0.00'}
              onChange={(e) => handleInputChange('cz', parseFloat(e.target.value) || 0)}
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' }
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <TextField
              label="Width"
              type="number"
              value={localValues.lx?.toFixed(2) || '1.00'}
              onChange={(e) => handleInputChange('lx', parseFloat(e.target.value) || 1)}
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Height"
              type="number"
              value={localValues.ly?.toFixed(2) || '1.00'}
              onChange={(e) => handleInputChange('ly', parseFloat(e.target.value) || 1)}
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Depth"
              type="number"
              value={localValues.lz?.toFixed(2) || '1.00'}
              onChange={(e) => handleInputChange('lz', parseFloat(e.target.value) || 1)}
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' }
              }}
            />
          </Grid>
        </Grid>

        {/* Yaw Input */}
        <TextField
          label="Yaw (degrees)"
          type="number"
          value={localValues.yaw ? (localValues.yaw * 180 / Math.PI).toFixed(1) : '0.0'}
          onChange={(e) => {
            const degrees = parseFloat(e.target.value) || 0
            const radians = degrees * Math.PI / 180
            handleInputChange('yaw', radians)
          }}
          fullWidth
          size="small"
          sx={{ mb: 2,
            '& .MuiInputLabel-root': { color: '#ccc' },
            '& .MuiInputBase-input': { color: 'white' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#666' }
          }}
        />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Duplicate">
            <IconButton 
              onClick={handleDuplicate}
              sx={{ color: 'white', border: '1px solid #666' }}
            >
              <DuplicateIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Delete">
            <IconButton 
              onClick={handleDelete}
              sx={{ color: 'white', border: '1px solid #666' }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={selectedBox.locked ? "Unlock" : "Lock"}>
            <IconButton 
              onClick={handleLockToggle}
              sx={{ 
                color: selectedBox.locked ? '#ff6b35' : 'white', 
                border: '1px solid #666' 
              }}
            >
              {selectedBox.locked ? <LockIcon /> : <UnlockIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  )
}

export default LabelHUD
